// PMax Ad Strength — Creative Excellence section, deterministic check.
// Distribution of asset-group ad strength + a per-group table, weakest first.

import type { EvidenceProps } from './registry';

type AdStrength =
  'EXCELLENT' | 'GOOD' | 'AVERAGE' | 'POOR' | 'PENDING' | 'NO_ADS' | 'UNSPECIFIED' | 'UNKNOWN';

interface AssetGroupRow {
  id?: string;
  name: string;
  campaignName: string;
  adStrength: AdStrength;
}

interface AdStrengthShape {
  totalAssetGroups: number;
  buckets: { EXCELLENT: number; GOOD: number; AVERAGE: number; POOR: number; OTHER: number };
  belowGoodCount: number;
  assetGroups: AssetGroupRow[];
}

const STRENGTH_LABEL: Record<AdStrength, string> = {
  EXCELLENT: 'Excellent',
  GOOD: 'Good',
  AVERAGE: 'Average',
  POOR: 'Poor',
  PENDING: 'Pending',
  NO_ADS: 'No ads',
  UNSPECIFIED: '—',
  UNKNOWN: '—'
};

// Map strength to a severity-flavored chip class: Good/Excellent are clean, Average is a
// small-opp amber, Poor (and pending/no-ads) read as the actionable bucket.
function strengthClass(s: AdStrength): string {
  if (s === 'EXCELLENT' || s === 'GOOD') return 'evidence__strength--good';
  if (s === 'AVERAGE') return 'evidence__strength--average';
  return 'evidence__strength--poor';
}

export const PmaxAdStrengthEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as AdStrengthShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Ad strength distribution
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalAssetGroups} asset groups
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.buckets.EXCELLENT.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Excellent</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.buckets.GOOD.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Good</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.buckets.AVERAGE.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Average</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>
              {(e.buckets.POOR + e.buckets.OTHER).toLocaleString()}
            </div>
            <div className='audience-coverage__lbl'>Poor / no ads</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Asset groups
          <span className='evidence__section-tag evidence__section-tag--context'>
            weakest first
          </span>
        </h4>
        <table className='evidence__table evidence__table--pmax-ad-strength'>
          <thead>
            <tr>
              <th>Asset group</th>
              <th>Campaign</th>
              <th>Ad strength</th>
            </tr>
          </thead>
          <tbody>
            {e.assetGroups.map(g => (
              <tr key={g.id ?? g.name}>
                <td>{g.name}</td>
                <td>{g.campaignName}</td>
                <td>
                  <span className={`evidence__strength ${strengthClass(g.adStrength)}`}>
                    {STRENGTH_LABEL[g.adStrength] ?? g.adStrength}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        Ad Strength is Google's composite signal for how complete and diverse an asset group's
        creative is. Tinuiti's bar is Good or Excellent for every active asset group — Average and
        Poor groups are the ones to prioritize. The Asset Group Coverage row breaks down exactly
        which components each group is missing.
      </p>
    </div>
  );
};
